import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, AlertTriangle, Trash2, Send } from "lucide-react";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// ConfirmDialog — szépen designolt megerősítő modal
// - Variant: "danger" (törlés piros), "default"
// - Hero ikon kör, leíró cím + szöveg, megerősítés gomb gradient
// ---------------------------------------------------------------------------
export default function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "Megerősítés",
  cancelLabel = "Mégse",
  onConfirm,
  loading = false,
  variant = "danger",
  testId,
}) {
  const isDanger = variant === "danger";
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent
        className="overflow-hidden border-0 p-0 sm:max-w-md"
        data-testid={testId}
      >
        {/* Hero */}
        <div className="relative overflow-hidden border-b border-slate-100 bg-gradient-to-br from-slate-50 to-white px-6 pb-5 pt-6 dark:border-border dark:from-card dark:to-card">
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.04] [background-image:radial-gradient(circle_at_1px_1px,_currentColor_1px,_transparent_0)] [background-size:14px_14px]"
            aria-hidden
          />
          <div className="relative flex items-start gap-4">
            <div
              className={cn(
                "grid h-12 w-12 shrink-0 place-items-center rounded-2xl shadow-[0_8px_22px_-10px_rgba(15,23,42,0.25)]",
                isDanger
                  ? "bg-gradient-to-br from-rose-500 to-rose-600 text-white"
                  : "bg-gradient-to-br from-indigo-500 to-indigo-700 text-white",
              )}
            >
              {isDanger ? (
                <AlertTriangle className="h-5 w-5" strokeWidth={2} />
              ) : (
                <Send className="h-5 w-5" strokeWidth={2} />
              )}
            </div>
            <div className="min-w-0 flex-1 pt-1">
              <AlertDialogHeader className="space-y-1.5 text-left">
                <AlertDialogTitle className="text-base font-semibold tracking-tight">
                  {title}
                </AlertDialogTitle>
                <AlertDialogDescription className="text-[13px] leading-relaxed text-slate-600 dark:text-muted-foreground">
                  {description}
                </AlertDialogDescription>
              </AlertDialogHeader>
            </div>
          </div>
        </div>

        <AlertDialogFooter className="flex items-center gap-2 bg-slate-50/60 px-6 py-3 dark:bg-card sm:justify-end">
          <AlertDialogCancel
            disabled={loading}
            className="mt-0 h-9"
            data-testid="confirm-cancel"
          >
            {cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={loading}
            onClick={(e) => {
              e.preventDefault();
              onConfirm?.();
            }}
            className={cn(
              "h-9 gap-1.5 font-semibold shadow-[0_4px_14px_-4px_rgba(15,23,42,0.3)]",
              isDanger
                ? "bg-gradient-to-br from-rose-500 to-rose-600 text-white hover:from-rose-600 hover:to-rose-700"
                : "bg-gradient-to-br from-emerald-600 to-emerald-700 text-white hover:from-emerald-700 hover:to-emerald-800",
            )}
            data-testid="confirm-action"
          >
            {loading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : isDanger ? (
              <Trash2 className="h-3.5 w-3.5" />
            ) : (
              <Send className="h-3.5 w-3.5" />
            )}
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
